import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useCallback } from 'react';
import UIList from 'UIComponents/list/UIList';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIFlex from 'UIComponents/layout/UIFlex';
import SortableListItem from './SortableListItem';
import DragAndDropSort from './DragAndDropSort';

/**
 * All child elements here must be passed with a sortKey prop
 * @param {string} ID of a custom view
 * @param {Array<JSX.Element>} children
 * @returns {any}
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getChildWithId = (childID, children) => children.find(child => child.props.sortKey === childID);

/**
 * Given an array of Custom View IDs create draggable components in the order of
 * the array
 * @param {string[]} order - Custom View IDs
 * @param {JSX.Element[]} children - React Components
 * @returns {JSX.Element[]} React Components in a user-defined order
 */
const createSortableList = (order, children) => order.map((viewId, index) => {
  const currentChild = getChildWithId(viewId, children);
  return /*#__PURE__*/_jsx(SortableListItem, {
    index: index,
    children: /*#__PURE__*/_jsx(currentChild.type, Object.assign({}, currentChild.props))
  }, viewId);
});
const reactChildrenWithSortKey = children => children.flatMap(child => child !== null && child && child.props && child.props.sortKey ? child : []);
const unorderedSortKeys = children => children.map(child => child.props.sortKey);
const SortableList = ({
  children,
  listId,
  listItemType,
  loading,
  onSort
}) => {
  const filteredChildren = reactChildrenWithSortKey(children);
  const displayOrder = unorderedSortKeys(filteredChildren);
  const dragEndHandler = useCallback(result => onSort(DragAndDropSort(result, displayOrder)), [displayOrder, onSort]);
  if (loading === true) {
    return /*#__PURE__*/_jsx(UIFlex, {
      align: "center",
      justify: "center",
      wrap: "nowrap",
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {
        size: "extra-small"
      })
    });
  }
  const orderedChildren = filteredChildren.length === 1 ? filteredChildren[0] : createSortableList(displayOrder, filteredChildren);
  return /*#__PURE__*/_jsx(DragDropContext, {
    onDragEnd: dragEndHandler,
    children: /*#__PURE__*/_jsx(Droppable, {
      droppableId: listId,
      type: listItemType,
      children: provided => /*#__PURE__*/_jsx("div", Object.assign({
        ref: provided.innerRef
      }, provided.droppableProps, {
        children: /*#__PURE__*/_jsxs(UIList, {
          children: [orderedChildren, " ", provided.placeholder]
        })
      }))
    })
  });
};
SortableList.displayName = 'SortableList';
export default SortableList;